if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton?.querySelector('span');
      this.spinner = this.querySelector('.loading-overlay__spinner');
      this.hideErrors = this.dataset.hideErrors === 'true';
      this.error = false;

      if (!this.form || !this.submitButton) return;

      const variantIdInput = this.variantIdInput;
      if (variantIdInput) variantIdInput.disabled = false;

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

      if (document.querySelector('cart-drawer')) {
        this.submitButton.setAttribute('aria-haspopup', 'dialog');
      }
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (!this.form || !this.submitButton) return;
      if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled) return;

      const variantIdInput = this.variantIdInput;
      if (!variantIdInput || !variantIdInput.value) {
        this.handleErrorMessage('Please select an available option before adding this item to your bag.');
        return;
      }
      variantIdInput.disabled = false;

      this.emitCommerce('imperial_cart_add_attempt', { variant_id: String(variantIdInput.value) });

      this.handleErrorMessage();
      this.error = false;
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      this.spinner?.classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      formData.set('id', variantIdInput.value);

      const cartRenderer =
        this.cart &&
        typeof this.cart.getSectionsToRender === 'function' &&
        typeof this.cart.renderContents === 'function'
          ? this.cart
          : null;

      if (cartRenderer) {
        try {
          const sections = cartRenderer
            .getSectionsToRender()
            .map((section) => section.id)
            .filter(Boolean);

          if (sections.length) {
            formData.append('sections', sections);
            formData.append('sections_url', window.location.pathname);
          }

          if (typeof cartRenderer.setActiveElement === 'function') {
            cartRenderer.setActiveElement(document.activeElement);
          }
        } catch (error) {
          console.error('Imperial Time cart renderer preparation failed; using cart-page fallback.', error);
          this.emitCommerce('imperial_cart_drawer_error', { stage: 'prepare', variant_id: String(variantIdInput.value) });
        }
      }

      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.description,
              message: response.message
            });

            this.handleErrorMessage(response.description || response.message || 'This item could not be added to your bag.');
            this.emitCommerce('imperial_cart_add_error', { stage: 'shopify_response', variant_id: String(formData.get('id')), error_code: String(response.status || 'shopify_error') });

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButtonText?.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            }
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response
          });

          this.error = false;
          this.emitCommerce('imperial_cart_add_success', { variant_id: String(formData.get('id')), cart_surface: cartRenderer ? 'drawer' : 'cart_page' });

          if (!cartRenderer) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          const quickAddModal = this.closest('quick-add-modal');
          const renderCart = () => {
            try {
              cartRenderer.renderContents(response);
            } catch (error) {
              console.error('Imperial Time cart drawer render failed; redirecting to cart.', error);
              this.emitCommerce('imperial_cart_drawer_error', { stage: 'render', variant_id: String(formData.get('id')) });
              window.location = window.routes?.cart_url || '/cart';
            }
          };

          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => setTimeout(renderCart), { once: true });
            quickAddModal.hide(true);
          } else {
            renderCart();
          }
        })
        .catch((error) => {
          console.error('Imperial Time add-to-cart request failed.', error);
          this.error = false;
          this.emitCommerce('imperial_cart_add_error', { stage: 'network', variant_id: String(formData.get('id')), error_code: 'network' });
          this.handleErrorMessage('We could not add this item to your bag. Please try again.');
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.spinner?.classList.add('hidden');
        });
    }

    emitCommerce(name, detail = {}) {
      const payload = Object.assign({ event: name }, detail);
      if (typeof window.imperialCommerceEvent === 'function') {
        window.imperialCommerceEvent(name, detail);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (this.hideErrors) return;

      this.errorMessageWrapper =
        this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;

      this.errorMessage =
        this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
      if (errorMessage && this.errorMessage) this.errorMessage.textContent = errorMessage;
    }

    toggleSubmitButton(disable = true, text) {
      if (!this.submitButton) return;

      if (disable) {
        this.submitButton.setAttribute('disabled', 'disabled');
        if (text && this.submitButtonText) this.submitButtonText.textContent = text;
      } else {
        this.submitButton.removeAttribute('disabled');
        if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
      }
    }

    get variantIdInput() {
      return this.form?.querySelector('[name=id]') || null;
    }
  });
}